import { useState } from "react";
import { Plus, Trash2, GripVertical, Image } from "lucide-react";
import ImagePicker from "@/components/common/ImagePicker";

export interface Highlight {
  title: string;
  description: string;
  image?: string;
}

interface HighlightsEditorProps {
  value: Highlight[];
  onChange: (value: Highlight[]) => void;
}

export default function HighlightsEditor({ value, onChange }: HighlightsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addHighlight = () => {
    onChange([...value, { title: "", description: "", image: "" }]);
    setExpandedIndex(value.length);
  };

  const removeHighlight = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const updateHighlight = (index: number, field: keyof Highlight, fieldValue: string) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [field]: fieldValue };
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Top atrakce
          </h3>
          <p className="text-sm text-gray-500">
            Přidejte nejzajímavější místa a zážitky v destinaci
          </p>
        </div>
        <button
          type="button"
          onClick={addHighlight}
          className="btn-secondary"
        >
          <Plus className="h-4 w-4" />
          Přidat atrakci
        </button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <Image className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">Zatím žádné atrakce</p>
          <button
            type="button"
            onClick={addHighlight}
            className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Přidat první atrakci
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((highlight, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              >
                <div className="flex items-center gap-3">
                  <GripVertical className="h-5 w-5 text-gray-400" />
                  {highlight.image && (
                    <img
                      src={highlight.image}
                      alt=""
                      className="h-10 w-10 rounded object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {highlight.title || `Atrakce ${index + 1}`}
                    </p>
                    {highlight.description && (
                      <p className="text-sm text-gray-500 truncate max-w-md">
                        {highlight.description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeHighlight(index);
                  }}
                  className="p-2 text-gray-400 hover:text-error-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {expandedIndex === index && (
                <div className="border-t border-gray-200 p-4 space-y-4 dark:border-gray-700">
                  <div>
                    <label className="label">Název *</label>
                    <input
                      type="text"
                      value={highlight.title}
                      onChange={(e) => updateHighlight(index, "title", e.target.value)}
                      className="input"
                      placeholder="např. Zátoka Ha Long"
                    />
                  </div>

                  <div>
                    <label className="label">Popis</label>
                    <textarea
                      value={highlight.description}
                      onChange={(e) => updateHighlight(index, "description", e.target.value)}
                      className="input"
                      rows={3}
                      placeholder="Krátký popis atrakce..."
                    />
                  </div>

                  <div>
                    <label className="label">Obrázek</label>
                    <ImagePicker
                      value={highlight.image || ""}
                      onChange={(url) => updateHighlight(index, "image", url)}
                      folder="destinations"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
